import { FaroTableFooterContainer } from "@components/common/faro-table/faro-table-footer-container";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { TablePaginationFooterData } from "@hooks/table/use-table-pagination-types";
import { Box } from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid-pro";
import { DASH } from "@utils/ui-utils";
import { ChevronLeftIcon, ChevronRightIcon } from "@flat-ui";

export interface FaroTableFooterProps {
  /** Defines the model for the pagination including the current page and the page size. */
  paginationModel: GridPaginationModel;

  /** The total number of items that are being paginated. */
  totalItems: number;

  /** Callback to change the page. */
  changePage: (page: number) => void;

  /** Pagination properties including indices and row count */
  paginationProps: TablePaginationFooterData;

  /**
   * Whether the next page button should be disabled manually
   * (in addition to it being disabled for the last page)
   */
  shouldDisableNextPage?: boolean;

  /** Tooltip to show for the next page button when it is disabled */
  disabledNextPageTooltip?: string;
}

/**
 * The footer of the table that shows the pagination information.
 */
export function FaroTableFooter({
  paginationModel,
  totalItems,
  changePage,
  paginationProps,
  shouldDisableNextPage = false,
  disabledNextPageTooltip = "Please load more items first",
}: FaroTableFooterProps): JSX.Element | null {
  /** Whether the total number of items in the backend is known for the current table */
  const isTotalKnown = totalItems !== -1;

  // Early exit when we don't have pagination because the number of items are less than the page size.
  // In that case we just show a label stating the total number of items.
  // For unknown totals we should show pagination controls
  if (isTotalKnown && totalItems <= paginationModel.pageSize) {
    return (
      <FaroTableFooterContainer>
        Total rows{" "}
        <Box component="strong" sx={{ marginLeft: "4px" }}>
          {totalItems}
        </Box>
      </FaroTableFooterContainer>
    );
  }

  const isOnLastPage = paginationProps.lastPage === paginationModel.page;

  return (
    <FaroTableFooterContainer>
      <Box component="strong">{`${paginationProps.from} ${DASH} ${paginationProps.to} `}</Box>
      <Box component="span" sx={{ marginX: "4px" }}>
        {isTotalKnown ? (
          <>
            of <var>{totalItems}</var>
          </>
        ) : (
          <>
            of more than <var>{paginationProps.to}</var>
          </>
        )}
      </Box>

      <SphereIconButton
        component={ChevronLeftIcon}
        isDisabled={paginationModel.page === 0}
        onClick={() => changePage(paginationModel.page - 1)}
        buttonSize="28px"
        iconSize="20px"
      />

      <Box component="span" sx={{ marginRight: "4px" }} />
      <SphereTooltip
        title={shouldDisableNextPage ? disabledNextPageTooltip : ""}
        shouldShowTooltip={shouldDisableNextPage && !isOnLastPage}
      >
        <SphereIconButton
          component={ChevronRightIcon}
          isDisabled={shouldDisableNextPage || isOnLastPage}
          onClick={() => changePage(paginationModel.page + 1)}
          buttonSize="28px"
          iconSize="20px"
        />
      </SphereTooltip>
    </FaroTableFooterContainer>
  );
}
